import { DiscountType } from "@/types/business/discount.type";
import { CategoryInterface, PriceInterface } from "@/interface/business/item.interface";
import { DiscountInterface } from "@/interface/business/discount.interface";
import { PriceMoney } from "@/modules/location";
import { DISCOUNT_TYPES } from "@/enums/business.enum";
import { DISCOUNT_APPLIED_ON } from "@/enums/orders.enum";
import { Translate as $t } from "@/plugins/translate";
import { FORM_TYPE } from "@/enums/apps.enum";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";

export default class Discount implements DiscountInterface {
  id: string = "";
  name: string = "";
  type: string = "";
  amount: number = 0;
  rate: number = 0;
  locations: Array<string> = [];
  color: string = "";
  pin_required: Boolean = false;
  after_tax: Boolean = false;
  item_applicable: Boolean = false;
  automatic: Boolean = false;
  start_date: string = "";
  end_date: string = "";
  days: Array<any> = [];
  items: Array<string> = [];
  categories: Array<CategoryInterface> = [];
  price: PriceInterface = new PriceMoney({ amount: 0 });
  isValidNow: boolean = false;
  disabled: boolean = false;
  applied_on: string = DISCOUNT_APPLIED_ON.SALE;
  isScheduled: boolean = false;
  formType: string = FORM_TYPE.CREATE;
  is_custom: boolean = false;
  error?: string = "";
  currency?: string = "";

  public static MODULE_NAME = "DISCOUNT";

  constructor(data?: DiscountType) {
    if (data) {
      Object.assign(this, data);
      this.price = new PriceMoney({ amount: data.amount || 0 });
      this.setIsValidNow();
    }

    if (data?.id) {
      this.formType = FORM_TYPE.EDIT;
    }

    this.currency = Storage.get(StorageConstants.BUSINESS)?.currency;
  }

  isFormEdit(): boolean {
    return this.formType === FORM_TYPE.EDIT;
  }

  isFormCreate(): boolean {
    return this.formType === FORM_TYPE.CREATE;
  }

  /**
   * handle on set the discount amount
   * @param amount
   */
  setAmount(amount: number): void {
    this.amount = amount * 1000;
    this.price = new PriceMoney({ amount: this.amount || 0 });
  }

  /**
   * handle on set the discount rate
   * @param rate
   */
  setRate(rate: number): void {
    this.rate = rate / 100;
  }

  /***
   * Set is Discount scheduled available now
   * @return {void | *}
   */
  private setIsValidNow(): void {
    if (!this.end_date || !this.start_date) {
      return;
    }

    this.isScheduled = true;

    const startDate = window.moment(this.start_date).local();
    const endDate = window.moment(this.end_date).local();

    const isValidNow = startDate?.isBefore() && endDate?.isAfter();

    // not valid now date set as disabled and isValidNow false
    if (!isValidNow) {
      this.isValidNow = isValidNow;
      this.disabled = !isValidNow;
      return;
    }

    // compare the day with today
    const getIsValidDay = (day: string): boolean => {
      return day.toLowerCase() === window.moment().format("dddd").toLowerCase();
    };

    // get today discount
    const today = this.days.find((currentDay) => getIsValidDay(currentDay.day.toLowerCase()));

    // if not today then it is not discount available today
    if (!today) {
      this.isValidNow = false;
      this.disabled = true;
      return;
    }

    // else if today have discount then check the now time is in range times
    const start = window.moment(today?.time?.start, "hh:mm");
    const end = window.moment(today?.time?.end, "hh:mm");

    this.isValidNow = end?.isAfter() && start?.isBefore();
    this.disabled = !this.isValidNow;
  }

  /**
   * Get Discount validity
   * @return string
   */
  getValidity(): string {
    const start = window.moment(this.start_date);
    const end = window.moment(this.end_date);

    if (end.isValid() && start.isValid()) {
      return `${start.format("DD MMM YY")} - ${end.format("DD MMM YY")}`;
    }

    return $t("unlimited");
  }

  /**
   * Set Discount disable
   * @return {void}
   */
  disableDiscount(): void {
    this.disabled = true;
  }

  /***
   * Prepare the discount value
   * @return {string}
   */
  public getInputDiscountValueLabel(): string {
    if (this.is_custom) {
      return this.getCustomDiscountLabel();
    }

    return this.getValue();
  }

  /***
   * Prepare the discount input label value
   * @return {string}
   */
  public getCustomDiscountLabel(): string {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return `Custom ${this.currency}`;
    }

    return `Custom %`;
  }

  /**
   * Get Value % or Price
   * @return {String} price || rate %
   */
  public getValue(): string {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return new PriceMoney({ amount: this.amount }).getPricePackage();
    }

    const rate = this.rate * 100;
    return `${rate.toFixed(1)}%`;
  }

  /**
   * Get Value input
   * @return {any}
   */
  public getInputValue(): any {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return this.amount / 1000;
    }

    return this.rate * 100;
  }

  /**
   * Get rate value
   * @return {String} rate %
   */
  public getRateValue(): string {
    if (this.type !== DISCOUNT_TYPES.PERCENTAGE) {
      return "0";
    }

    return this.getValue();
  }

  /**
   * Get amount value
   * @return {String} rate %
   */
  public getAmountValue(): string {
    if (this.type !== DISCOUNT_TYPES.FIXED) {
      return "0";
    }

    return this.getValue();
  }

  /**
   * Update the applied_on
   * @param type
   */
  public setAppliedOn(type: string): void {
    this.applied_on = type;
  }

  /**
   * Get discount type value
   * @return {string}
   */
  public getTypeValue(): string {
    return this.type === "FIXED" ? $t("fixed") : $t("percentage");
  }

  /**
   * Get the apply to value
   * @return {string}
   */
  public getApplyTo(): string {
    if (this.item_applicable && !this.items.length && !this.categories.length) {
      return $t("any_item");
    }

    if (this.item_applicable && !this.items.length && !!this.categories.length) {
      return $t("specific_category");
    }

    if (this.item_applicable && !!this.items.length && !this.categories.length) {
      return $t("specific_item");
    }

    if (!this.item_applicable) {
      return $t("total_bill");
    }
    return "";
  }

  /**
   * Get days list
   * @return {any} daysObject
   */
  public getDays(): any {
    // const availability = {};
    // return this.days.map(({ day, time }) => {
    //   // availability.day =  day.substring(0, 3).toLowerCase()
    //   return {
    //     day: day.substring(0, 3).toLowerCase(),
    //     time: `${time.start} - ${time.end}`,
    //   };
    // });
  }

  /**
   * Rest Value
   * @return {void}
   */
  public resetValue(): void {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      this.amount = 0;
      return;
    }

    // if this.type is DISCOUNT_TYPES.PERCENTAGE
    this.rate = 0;
  }
}
